"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFetchEligibleServiceTariffsQuery = exports.useDeleteServiceChangeEventMutation = exports.useFetchServiceAuditLogQuery = exports.useFetchServiceHistoryQuery = exports.useLazyFetchServicesForAreaQuery = exports.useFetchServicesForAreaQuery = exports.useClearMeterMeteringMethodMutation = exports.useSetMeterMeteringMethodMutation = exports.useRemoveAreaFromServiceMutation = exports.useFetchMeterRequirementQuery = exports.useSetAreaRenderingMutation = exports.useAddAreaToServiceMutation = exports.useSetServiceLevelMutation = exports.useUpdateServiceMutation = exports.useDeleteServiceMutation = exports.useLazyFetchServiceQuery = exports.useFetchServiceQuery = exports.useSearchServicesQuery = exports.useFetchClientServicesQuery = exports.useCreateServiceMutation = exports.usePreviewServiceMunicipalityQuery = exports.serviceApi = void 0;
// api/serviceApi.ts
var react_1 = require("@reduxjs/toolkit/query/react");
var baseQueryWithReauth_1 = require("../../utils/baseQueryWithReauth");
exports.serviceApi = (0, react_1.createApi)({
    reducerPath: "serviceApi",
    baseQuery: (0, baseQueryWithReauth_1.baseQueryWithReauth)(process.env.LETS_CUE_API),
    tagTypes: ["services"],
    endpoints: function (builder) { return ({
        createService: builder.mutation({
            query: function (obj) { return ({
                url: "/v1/service/create",
                method: "POST",
                body: obj,
            }); },
            invalidatesTags: ["services"],
        }),
        fetchClientServices: builder.query({
            query: function (obj) { return ({
                url: "/v1/service/all",
                method: "GET",
                params: {
                    clientId: obj.clientId,
                    buildingId: obj.buildingId,
                    serviceType: obj.serviceType,
                    page: obj.page,
                    limit: obj.limit,
                },
            }); },
            providesTags: ["services"],
        }),
        searchServices: builder.query({
            query: function (obj) {
                var params = {
                    clientId: obj.clientId,
                    page: obj.page || 1,
                    limit: obj.limit || 10,
                };
                if (obj.search)
                    params.search = obj.search;
                if (obj.buildingId)
                    params.buildingId = obj.buildingId;
                if (obj.serviceType)
                    params.serviceType = obj.serviceType;
                if (obj.isActive !== undefined)
                    params.isActive = obj.isActive;
                return {
                    url: "/v1/service/all",
                    method: "GET",
                    params: params,
                };
            },
            providesTags: ["services"],
        }),
        fetchService: builder.query({
            query: function (serviceId) { return ({
                url: "/v1/service/fetch",
                method: "GET",
                params: {
                    _id: serviceId,
                },
            }); },
            providesTags: ["services"],
        }),
        updateService: builder.mutation({
            query: function (obj) { return ({
                url: "/v1/service/update",
                method: "PUT",
                body: obj,
            }); },
            invalidatesTags: ["services"],
        }),
        // Flip a service between BUILDING and AREA. Deliberately not updateService, which requires an
        // edit classification, effective date and reason — too heavy for a scoping toggle.
        setServiceLevel: builder.mutation({
            query: function (obj) { return ({
                url: "/v1/service/level",
                method: "PATCH",
                body: obj,
            }); },
            invalidatesTags: ["services"],
        }),
        // Opt ONE area in to an AREA-level service. Additive — other areas keep their membership.
        // Do NOT use updateService with an areaId for this: that sets a single field and silently
        // un-links whichever area the service was attached to before.
        addAreaToService: builder.mutation({
            query: function (obj) { return ({
                url: "/v1/service/area/add",
                method: "POST",
                body: obj,
            }); },
            invalidatesTags: ["services"],
        }),
        // Opt ONE area out. Removes only this area's membership.
        removeAreaFromService: builder.mutation({
            query: function (_a) {
                var serviceId = _a.serviceId, clientId = _a.clientId, areaId = _a.areaId, performedBy = _a.performedBy, editClassification = _a.editClassification, effectiveDate = _a.effectiveDate, effectiveTime = _a.effectiveTime, reason = _a.reason;
                return ({
                    url: "/v1/service/area/remove",
                    method: "DELETE",
                    params: __assign(__assign(__assign(__assign({ serviceId: serviceId, clientId: clientId, areaId: areaId, performedBy: performedBy }, (editClassification ? { editClassification: editClassification } : {})), (effectiveDate ? { effectiveDate: effectiveDate } : {})), (effectiveTime ? { effectiveTime: effectiveTime } : {})), (reason ? { reason: reason } : {})),
                });
            },
            invalidatesTags: ["services"],
        }),
        /**
         * Whether an area can measure a service, and what meter it is missing. Consulted before a
         * consumption-based service is added to an area (ticket point 2).
         */
        fetchMeterRequirement: builder.query({
            query: function (_a) {
                var clientId = _a.clientId, serviceId = _a.serviceId, areaId = _a.areaId;
                return ({
                    url: "/v1/service/meter-requirement",
                    method: "GET",
                    params: { clientId: clientId, serviceId: serviceId, areaId: areaId },
                });
            },
            providesTags: ["services"],
        }),
        setAreaRendering: builder.mutation({
            query: function (obj) { return ({
                url: "/v1/service/area-rendering",
                method: "POST",
                body: obj,
            }); },
            invalidatesTags: ["services"],
        }),
        // GEB-1: override the metering method for ONE meter. Upserts a single entry — other meters'
        // overrides are untouched.
        setMeterMeteringMethod: builder.mutation({
            query: function (obj) { return ({
                url: "/v1/service/meter-method",
                method: "POST",
                body: obj,
            }); },
            invalidatesTags: ["services"],
        }),
        // Drop ONE meter's override so it falls back to the service default.
        clearMeterMeteringMethod: builder.mutation({
            query: function (_a) {
                var serviceId = _a.serviceId, clientId = _a.clientId, meterId = _a.meterId, performedBy = _a.performedBy;
                return ({
                    url: "/v1/service/meter-method",
                    method: "DELETE",
                    params: { serviceId: serviceId, clientId: clientId, meterId: meterId, performedBy: performedBy },
                });
            },
            invalidatesTags: ["services"],
        }),
        // Services applying to one area: building-level (mandatory) plus area-level ones it opted into.
        fetchServicesForArea: builder.query({
            query: function (_a) {
                var clientId = _a.clientId, buildingId = _a.buildingId, areaId = _a.areaId;
                return ({
                    url: "/v1/service/for-area",
                    method: "GET",
                    params: { clientId: clientId, buildingId: buildingId, areaId: areaId },
                });
            },
            providesTags: ["services"],
        }),
        deleteService: builder.mutation({
            query: function (_a) {
                var serviceId = _a.serviceId, performedBy = _a.performedBy, reason = _a.reason;
                return ({
                    url: "/v1/service/remove",
                    method: "DELETE",
                    params: __assign({ _id: serviceId, performedBy: performedBy }, (reason ? { reason: reason } : {})),
                });
            },
            invalidatesTags: ["services"],
        }),
        fetchServiceHistory: builder.query({
            query: function (serviceId) { return ({
                url: "/v1/service/history",
                method: "GET",
                params: { _id: serviceId },
            }); },
            providesTags: function (_result, _err, serviceId) { return [{ type: "services", id: "history:".concat(serviceId) }]; },
        }),
        fetchServiceAuditLog: builder.query({
            query: function (_a) {
                var serviceId = _a.serviceId, action = _a.action, includeDeleted = _a.includeDeleted;
                return ({
                    url: "/v1/service/audit-log",
                    method: "GET",
                    params: __assign(__assign({ _id: serviceId }, (action ? { action: action } : {})), (includeDeleted ? { includeDeleted: true } : {})),
                });
            },
            providesTags: function (_result, _err, arg) { return [{ type: "services", id: "audit:".concat(arg.serviceId) }]; },
        }),
        deleteServiceChangeEvent: builder.mutation({
            query: function (_a) {
                var eventId = _a.eventId, performedBy = _a.performedBy, reason = _a.reason;
                return ({
                    url: "/v1/service/audit-log/event",
                    method: "DELETE",
                    body: { eventId: eventId, performedBy: performedBy, reason: reason },
                });
            },
            invalidatesTags: function (_result, _err, arg) { return [
                { type: "services", id: "audit:".concat(arg.serviceId) },
                { type: "services", id: "history:".concat(arg.serviceId) },
            ]; },
        }),
        /**
         * RF-1 B7.14/B7.15: what this service's municipality would resolve to, and what it would clash
         * with — so the form can warn before the user saves rather than after. Writes nothing.
         */
        previewServiceMunicipality: builder.query({
            query: function (obj) { return ({
                url: "/v1/service/municipality-preview",
                method: "GET",
                params: __assign(__assign(__assign(__assign({ clientId: obj.clientId, buildingId: obj.buildingId }, (obj.serviceId ? { serviceId: obj.serviceId } : {})), (obj.municipalityId ? { municipalityId: obj.municipalityId } : {})), (obj.supplySource ? { supplySource: obj.supplySource } : {})), (obj.supplier ? { supplier: obj.supplier } : {})),
            }); },
        }),
        // The tariff families + rate-card rows applicable to a Bulk Electricity service (read-only).
        fetchEligibleServiceTariffs: builder.query({
            query: function (_a) {
                var serviceId = _a.serviceId, clientId = _a.clientId;
                return ({
                    url: "/v1/service/".concat(serviceId, "/eligible-tariffs"),
                    method: "GET",
                    params: { clientId: clientId },
                });
            },
            providesTags: function (_result, _err, arg) { return [{ type: "services", id: "eligible:".concat(arg.serviceId) }]; },
        }),
    }); },
});
exports.usePreviewServiceMunicipalityQuery = exports.serviceApi.usePreviewServiceMunicipalityQuery, exports.useCreateServiceMutation = exports.serviceApi.useCreateServiceMutation, exports.useFetchClientServicesQuery = exports.serviceApi.useFetchClientServicesQuery, exports.useSearchServicesQuery = exports.serviceApi.useSearchServicesQuery, exports.useFetchServiceQuery = exports.serviceApi.useFetchServiceQuery, exports.useLazyFetchServiceQuery = exports.serviceApi.useLazyFetchServiceQuery, exports.useDeleteServiceMutation = exports.serviceApi.useDeleteServiceMutation, exports.useUpdateServiceMutation = exports.serviceApi.useUpdateServiceMutation, exports.useSetServiceLevelMutation = exports.serviceApi.useSetServiceLevelMutation, exports.useAddAreaToServiceMutation = exports.serviceApi.useAddAreaToServiceMutation, exports.useSetAreaRenderingMutation = exports.serviceApi.useSetAreaRenderingMutation, exports.useFetchMeterRequirementQuery = exports.serviceApi.useFetchMeterRequirementQuery, exports.useRemoveAreaFromServiceMutation = exports.serviceApi.useRemoveAreaFromServiceMutation, exports.useSetMeterMeteringMethodMutation = exports.serviceApi.useSetMeterMeteringMethodMutation, exports.useClearMeterMeteringMethodMutation = exports.serviceApi.useClearMeterMeteringMethodMutation, exports.useFetchServicesForAreaQuery = exports.serviceApi.useFetchServicesForAreaQuery, exports.useLazyFetchServicesForAreaQuery = exports.serviceApi.useLazyFetchServicesForAreaQuery, exports.useFetchServiceHistoryQuery = exports.serviceApi.useFetchServiceHistoryQuery, exports.useFetchServiceAuditLogQuery = exports.serviceApi.useFetchServiceAuditLogQuery, exports.useDeleteServiceChangeEventMutation = exports.serviceApi.useDeleteServiceChangeEventMutation, exports.useFetchEligibleServiceTariffsQuery = exports.serviceApi.useFetchEligibleServiceTariffsQuery;
